import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { connect, useSelector } from 'react-redux';
import debounce from 'lodash/debounce';

import { ARCHIVE_EXPORT } from '@common/constants/batchReportsConstants';

import { showAlert } from '../store/actions';
import { showJobErrorsModal } from '../../menu/jobsMenu/store/actions';

import { useOnJimJobComplete, useOnJimJobFail, useOnJimJobSuccess } from './hooks';
import JimProgressBar from './jimProgressBar.component';
import { isAddingJobOfTypeSelectorFactory, jobByIdSelector, lastJobOfTypeSelectorFactory } from './store/selectors';
import { JobConfig } from './propTypes';
import { Job } from '../../menu/jobsMenu/types';
import { JimJobErrorAlert, JimJobSuccessAlert } from './JimJobAlerts.component';
import alertTypes from '../alertTypes';
import { useMarkJimNotificationsAsSeen } from '../mutations/jimJobs';
import { inProgressStatuses } from './constants';


// eslint-disable-next-line no-empty-function
const noop = () => {};

const HIDE_PROGRESS_BAR_AFTER_MS = 1.5 * 1000;

const useForceShowProgressBar = (job: Job) => {
  const [showProgressBar, setShowProgressbar] = useState(false);

  const hideProgressBar = useMemo(
    () => debounce(() => setShowProgressbar(false), HIDE_PROGRESS_BAR_AFTER_MS),
    []
  );

  useEffect(() => {
    if (job && inProgressStatuses.includes(job.status)) {
      setShowProgressbar(true);
    }
  }, [job]);

  useOnJimJobComplete(job, () => {
    hideProgressBar();
  });

  return showProgressBar;
};


type JimJobInfoProps = {
  jobConfig: JobConfig,
  onJobComplete?: (job: Job) => void,
  onJobSuccess?: (job: Job) => void,
  onJobFail?: (job: Job) => void,
  showProgressBar?: boolean,
  showAlert: (alertComponent: React.ReactElement, type: string) => void,
  showJobErrorsModal: (errors: Job['returnedError']) => void,
  forceProgressBar?: boolean,
  forceSelectionById?: boolean,
  jobId?: Job['jobId'] | null,
  autoMarkNotificationAsSeen?: boolean,
}

const JimJobInfo: React.FC<JimJobInfoProps> = ({
  jobConfig: {
    type,
    successMessage,
    errorMessage,
    progressBarText,
  },
  onJobComplete = noop,
  onJobFail = noop,
  onJobSuccess = noop,
  showProgressBar,
  showAlert,
  showJobErrorsModal,
  forceSelectionById = false,
  jobId,
  autoMarkNotificationAsSeen = true,
}: JimJobInfoProps) => {
  const jobSelectorFactory = () => forceSelectionById
    ? jobByIdSelector(jobId)
    : lastJobOfTypeSelectorFactory(type);

  const job = useSelector(jobSelectorFactory());
  const jobCreationInProgress = useSelector(isAddingJobOfTypeSelectorFactory(type));
  const { mutate: markNotificationAsSeen } = useMarkJimNotificationsAsSeen();
  const forceShowProgressBar = useForceShowProgressBar(job);

  useOnJimJobComplete(job, useCallback((job: Job) => {
    onJobComplete(job);
    if (autoMarkNotificationAsSeen) {
      markNotificationAsSeen([job.jobId]);
    }
  }, [onJobComplete, markNotificationAsSeen, autoMarkNotificationAsSeen]));

  useOnJimJobSuccess(job, useCallback((job: Job) => {
    onJobSuccess(job);
    if (successMessage) {
      showAlert(
        (
          <JimJobSuccessAlert
            title={successMessage}
            job={job}
            isArchive={job.jobType === ARCHIVE_EXPORT}
          />
        ),
        alertTypes.SUCCESS
      );
    }
  }, [
    onJobSuccess,
    successMessage,
    showAlert,
  ]));

  useOnJimJobFail(job, useCallback((job: Job) => {
    onJobFail(job);
    if (errorMessage) {
      showAlert(
        (
          <JimJobErrorAlert
            title={errorMessage}
            showErrors={showJobErrorsModal}
            errors={job.returnedError}
          />
        ),
        alertTypes.DANGER
      );
    }
  }, [onJobFail, errorMessage, showAlert, showJobErrorsModal]));

  // handle case in which job is not created
  if (jobCreationInProgress) {
    return (
      <div className="progress job-creation-in-progress">
        Job creation in progress... Please do not close the browser&apos;s tab.
      </div>
    );
  }

  if (showProgressBar) {
    return (
      <JimProgressBar
        job={job}
        text={progressBarText}
        forceShowProgressBar={forceShowProgressBar}
      />
    );
  }
  return null;
};


export default connect(null, {
  showAlert,
  showJobErrorsModal,
})(JimJobInfo);
